import React, { useState, useMemo, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useInvitation } from "./hooks/useInvitation";
import { Button, Form, Input, Spinner } from "@heroui/react";
import {
  passwordRequirements,
  validatePassword,
  validatePasswordMatch,
} from "./utils/passwordValidation"
import type { PasswordForm } from "./inviteTypes";
import { faEyeSlash, faEye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { trackException, trackInfo, LogLevel } from "../../shared/services/appInsights";
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from "../../shared/services/appInsightsConstants";

const InvitePage: React.FC = () => {
  const { guid } = useParams<{ guid: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t");

  const [form, setForm] = useState<PasswordForm>({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasInteracted, setHasInteracted] = useState({
    password: false,
    confirmPassword: false,
  });

  const navigate = useNavigate();

  const { validationState, acceptState, acceptInvitation } = useInvitation(
    guid || "",
    token || ""
  );

  // Track page visit
  useEffect(() => {
    trackInfo("Invite page visited", {
      component: AppInsightsComponents.INVITE_PAGE,
      action: AppInsightsActions.PAGE_VISIT,
      hasGuid: !!guid,
      hasToken: !!token,
      guid: guid ? "provided" : "missing" // Don't log actual GUID for privacy
    });
  }, [guid, token]);

  // Real-time password validation
  const passwordValidation = useMemo(() => {
    if (!form.password) return { isValid: false, errors: [] };
    return validatePassword(form.password);
  }, [form.password]);

  const passwordMatchValidation = useMemo(() => {
    if (!form.confirmPassword) return { isValid: true };
    return validatePasswordMatch(form.password, form.confirmPassword);
  }, [form.password, form.confirmPassword]);

  const isValid =
    passwordValidation.isValid &&
    passwordMatchValidation.isValid &&
    form.password &&
    form.confirmPassword;

  const handleInputChange = (field: keyof PasswordForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setHasInteracted((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || !validationState.email) return;

    // Track invite acceptance attempt
    trackInfo("User attempting to accept invitation", {
      component: AppInsightsComponents.INVITE_PAGE,
      action: AppInsightsActions.INVITE_ACCEPT_ATTEMPT,
      email: validationState.email || "unknown",
      hasValidPassword: passwordValidation.isValid,
      hasMatchingPassword: passwordMatchValidation.isValid
    });

    try {
      await acceptInvitation({
        password: form.password,
        confirmPassword: form.confirmPassword,
      });

    } catch (error) {
      // Track invitation acceptance failure
      trackException(
        error as Error,
        LogLevel.Error,
        {
          component: AppInsightsComponents.INVITE_PAGE,
          action: AppInsightsActions.INVITE_ACCEPT_FAILURE,
          errorType: AppInsightsErrorTypes.UNEXPECTED_ERROR,
          email: validationState.email || "unknown",
          errorMessage: (error as Error).message
        }
      );
    }
  };

  const renderValidationState = () => {
    switch (validationState.status) {
      //switch ("invalid") {
      case "validating":
        return (
          <div className="form col-span-6 md:col-span-3 lg:col-span-4 flex items-center justify-center min-h-screen py-8">
            <div className="flex flex-col items-center justify-center mx-auto">
              <Spinner
                size="lg"
                label="Validating invitation..."
                classNames={{ label: "text-md" }}
                variant="simple"
              />
              <p className="mt-2 text-gray-600">
                Please wait while we verify your invitation link.
              </p>
            </div>
          </div>
        );

      case "valid":
        if (acceptState.status === "success") {
          return (
            <div className="form col-span-6 md:col-span-3 lg:col-span-4 flex items-center justify-center min-h-screen py-8">
              <div className="flex flex-col items-center justify-center mx-auto">
                <h3 className="mb-2 text-2xl font-bold text-gray-900">
                  Account Setup Complete!
                </h3>

                <p className="mb-6 text-gray-600">
                  Your account has been successfully created. You can now login
                  with your credentials.
                </p>

                <Button
                  size="md"
                  className="rounded-none"
                  color="primary"
                  onPress={() => {
                    trackInfo("User navigating to login after successful invite", {
                      component: AppInsightsComponents.INVITE_PAGE,
                      action: AppInsightsActions.NAVIGATION_SUCCESS,
                      targetPage: "login",
                      email: validationState.email || "unknown"
                    });
                    navigate("/account/login");
                  }}
                >
                  Go to Login
                </Button>
              </div>
            </div>
          );
        } else if (acceptState.status === "error") {
          return (
            <div className="form col-span-6 md:col-span-3 lg:col-span-4 flex items-center justify-center min-h-screen py-8">
              <div className="flex flex-col items-center justify-center mx-auto">
                <h3 className="mb-2 text-2xl font-bold text-gray-900">
                  Account Setup Failed!
                </h3>
                <p className="mb-3 text-gray-600">{acceptState.errorMessage}</p>
                <p className="mb-3 text font-bold text-gray-900">
                  Please contact MSI for assistance.
                </p>
                <div className="flex flex-row items-center justify-center space-x-4">
                  <Button
                    size="md"
                    className="rounded-none"
                    color="primary"
                    onPress={() =>
                      (window.location.href = "https://www.msiltd.com/")
                    }
                  >
                    Go to MSI Website
                  </Button>
                  <Button
                    size="md"
                    className="rounded-none"
                    color="primary"
                    onPress={() => window.location.reload()}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="form col-span-6 md:col-span-3 lg:col-span-4">
            <div className="grid grid-cols-8">
              <div className="col-span-3 sm:col-span-3 md:col-span-3 lg:col-span-2 xl:col-span-2 2xl:col-span-2">
                <h6 className="whitespace-nowrap">Complete Account Setup</h6>
              </div>
              <div className="m-1.5 col-span-8 sm:col-span-8 md:col-span-8 lg:col-span-5 xl:col-span-4 2xl:col-span-3">
                <Input
                  isReadOnly
                  label="Email Address"
                  labelPlacement="outside"
                  name="emailAddress"
                  placeholder="Enter your email address"
                  type="email"
                  value={validationState.email || ""}
                  classNames={{
                    inputWrapper: "rounded-none border-1 ",
                    label: "label",
                  }}
                />

                <br />

                <Form onSubmit={handleSubmit}>
                  <Input
                    isRequired
                    errorMessage={
                      form.password === "" && hasInteracted.password
                        ? "Password is required"
                        : form.password !== "" && !passwordValidation.isValid
                        ? "Please meet all password requirements"
                        : undefined
                    }
                    isInvalid={
                      (!passwordValidation.isValid && form.password !== "") ||
                      (form.password === "" && hasInteracted.password)
                    }
                    label="New Password"
                    labelPlacement="outside"
                    name="password"
                    placeholder="Enter your new password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    classNames={{
                      inputWrapper: `rounded-none border-1 ${
                        form.password && !passwordValidation.isValid
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300"
                      }`,
                      label: "label",
                    }}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    isReadOnly={acceptState.status === "submitting"}
                    endContent={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 px-4 flex items-center text-sm text-gray-600 hover:text-gray-800"
                        disabled={acceptState.status === "submitting"}
                      >
                        {showPassword ? (
                          <FontAwesomeIcon icon={faEyeSlash} />
                        ) : (
                          <FontAwesomeIcon icon={faEye} />
                        )}
                      </button>
                    }
                  />

                  {/* Password Requirements */}
                  <div className="mt-1 mb-2 space-y-2">
                    {passwordRequirements.map((requirement, index) => {
                      const isRequirementValid = requirement.test(
                        form.password
                      );
                      return (
                        <div key={index} className="flex items-center text-sm">
                          <div
                            className={`w-3 h-3 rounded-full mr-3 ${
                              form.password === ""
                                ? "bg-gray-300"
                                : isRequirementValid
                                ? "bg-green-500"
                                : "bg-red-500"
                            }`}
                          />
                          <span
                            className={
                              form.password === ""
                                ? "text-gray-500"
                                : isRequirementValid
                                ? "text-green-600"
                                : "text-red-600"
                            }
                          >
                            {requirement.message}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <Input
                    isRequired
                    errorMessage={
                      form.confirmPassword === "" &&
                      hasInteracted.confirmPassword
                        ? "Confirm password is required"
                        : form.confirmPassword !== "" &&
                          !passwordMatchValidation.isValid
                        ? "Passwords do not match"
                        : !passwordValidation.isValid && hasInteracted.password
                        ? "Please meet password requirements first"
                        : undefined
                    }
                    isInvalid={
                      (!passwordMatchValidation.isValid &&
                        form.confirmPassword !== "") ||
                      (form.confirmPassword === "" &&
                        hasInteracted.confirmPassword) ||
                      (passwordValidation.isValid === false &&
                        hasInteracted.password)
                    }
                    label="Confirm Password"
                    labelPlacement="outside"
                    name="password"
                    placeholder="Re-enter your password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={form.confirmPassword}
                    classNames={{
                      inputWrapper: `rounded-none border-1 ${
                        form.confirmPassword &&
                        (!passwordMatchValidation.isValid ||
                          !passwordValidation.isValid)
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300"
                      }`,
                      label: "label",
                    }}
                    onChange={(e) =>
                      handleInputChange("confirmPassword", e.target.value)
                    }
                    isReadOnly={acceptState.status === "submitting"}
                    endContent={
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute inset-y-0 right-0 px-4 flex items-center text-sm text-gray-600 hover:text-gray-800"
                        disabled={acceptState.status === "submitting"}
                      >
                        {showConfirmPassword ? (
                          <FontAwesomeIcon icon={faEyeSlash} />
                        ) : (
                          <FontAwesomeIcon icon={faEye} />
                        )}
                      </button>
                    }
                  />

                  {/* Password Match Validation */}
                  {form.confirmPassword && (
                    <div className="mt-0 flex items-center text-sm">
                      <div
                        className={`w-3 h-3 rounded-full mr-3 ${
                          passwordMatchValidation.isValid
                            ? "bg-green-500"
                            : "bg-red-500"
                        }`}
                      />
                      <span
                        className={
                          passwordMatchValidation.isValid
                            ? "text-green-600"
                            : "text-red-600"
                        }
                      >
                        {passwordMatchValidation.isValid
                          ? "Passwords match"
                          : passwordMatchValidation.error}
                      </span>
                    </div>
                  )}

                  <div className="flex w-full gap-2 justify-between items-center">
                    <div className="space-x-4">
                      <a
                        className="navigate-link"
                        href="https://www.msiltd.com"
                      >
                        Go to Main Website
                      </a>
                    </div>
                    <div>
                      <Button
                        isDisabled={
                          acceptState.status === "submitting" ||
                          passwordMatchValidation.isValid === false ||
                          passwordValidation.isValid === false ||
                          form.password === "" ||
                          form.confirmPassword === ""
                        }
                        size="md"
                        className="rounded-none"
                        color="primary"
                        type="submit"
                      >
                        {acceptState.status === "submitting"
                          ? "Setting up..."
                          : "Save Password"}
                      </Button>
                    </div>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        );

      case "invalid":
        return (
          <div className="form col-span-6 md:col-span-3 lg:col-span-4 flex items-center justify-center min-h-screen py-8">
            <div className="flex flex-col items-center justify-center mx-auto">
              <h3 className="mb-2 text-2xl font-bold text-gray-900">
                Invalid Invitation!
              </h3>
              <p className="mb-3 text-gray-600">
                {validationState.errorMessage}
              </p>
              <p className="mb-3 text font-bold text-gray-900">
                Please contact MSI for assistance.
              </p>
              <div className="flex flex-row items-center justify-center space-x-4">
                <Button
                  size="md"
                  className="rounded-none"
                  color="primary"
                  onPress={() => navigate("/account/login")}
                >
                  Go to Login
                </Button>
                <Button
                  size="md"
                  className="rounded-none"
                  color="primary"
                  onPress={() =>
                    (window.location.href = "https://www.msiltd.com/")
                  }
                >
                  Go to MSI Website
                </Button>
              </div>
            </div>
          </div>
        );

      case "error":
        return (
          <div className="form col-span-6 md:col-span-3 lg:col-span-4 flex items-center justify-center min-h-screen py-8">
            <div className="flex flex-col items-center justify-center mx-auto">
              <h3 className="mb-2 text-2xl font-bold text-gray-900">
                Something went wrong!
              </h3>
              <p className="mb-6 text-gray-600">
                {validationState.errorMessage}
              </p>
              <div className="flex flex-row items-center justify-center space-x-4">
                <Button
                  size="md"
                  className="rounded-none"
                  color="primary"
                  onPress={() => navigate("/account/login")}
                >
                  Go to Login
                </Button>
                <Button
                  size="md"
                  className="rounded-none"
                  color="primary"
                  onPress={() =>
                    (window.location.href = "https://www.msiltd.com/")
                  }
                >
                  Go to MSI Website
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="form col-span-6 md:col-span-3 lg:col-span-4 flex items-center justify-center min-h-screen py-8">
            <div className="flex flex-col items-center justify-center mx-auto">
              <h3 className="mb-2 text-2xl font-bold text-gray-900">
                Something went wrong!
              </h3>
              <p className="mb-6 text-gray-600">
                Please contact MSI or try again later.
              </p>
              <div className="flex flex-row items-center justify-center space-x-4">
                <Button
                  size="md"
                  className="rounded-none"
                  color="primary"
                  onPress={() => navigate("/account/login")}
                >
                  Go to Login
                </Button>
                <Button
                  size="md"
                  className="rounded-none"
                  color="primary"
                  onPress={() =>
                    (window.location.href = "https://www.msiltd.com/")
                  }
                >
                  Go to MSI Website
                </Button>
              </div>
            </div>
          </div>
        );
    }
  };

  return <>{renderValidationState()}</>;
};

export default InvitePage;
